import {
  type CreateSiteDraftInput,
  type SiteDocument,
  type SiteRevision,
  type SiteSummary,
  type UpdateSiteSettingsInput,
  createSiteDraftInputSchema,
  siteSummarySchema,
  updateSiteSettingsInputSchema,
} from '../domains/site/schema';
import { buildInitialSiteDocument, saveSiteDocumentRevision } from './site-documents';
import { ensureSiteWorkspaceFromDocument } from './site-workspaces';
import { apiRequest } from './api-client';

export type { SiteSummary, CreateSiteDraftInput, UpdateSiteSettingsInput };

export async function listSites(): Promise<SiteSummary[]> {
  const data = await apiRequest<unknown[]>('/api/sites');
  return data.map((row) => siteSummarySchema.parse(row));
}

export async function getSiteById(siteId: string): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/sites/${encodeURIComponent(siteId)}`);
  return siteSummarySchema.parse(data);
}

export async function getPublishedSiteBySlug(slug: string): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/public/sites/by-slug/${encodeURIComponent(slug)}`);
  return siteSummarySchema.parse(data);
}

export async function countPublishedSites(): Promise<number> {
  const data = await apiRequest<{ publishedSites: number }>('/api/sites/usage');
  return data.publishedSites;
}

async function createDraftSiteRecord(siteName: string): Promise<SiteSummary> {
  const trimmedSiteName = siteName.trim();
  if (!trimmedSiteName) {
    throw new Error('サイト名を入力してください。');
  }
  const site = await apiRequest<unknown>('/api/sites', {
    method: 'POST',
    body: { siteName: trimmedSiteName },
  });
  return siteSummarySchema.parse(site);
}

async function createDraftSiteWithRevision(
  input: CreateSiteDraftInput,
  document: SiteDocument,
  revisionSummary: string,
): Promise<{ site: SiteSummary; document: SiteDocument; revision: SiteRevision }> {
  const validated = createSiteDraftInputSchema.parse(input);
  const site = await createDraftSiteRecord(validated.siteName);

  try {
    const result = await saveSiteDocumentRevision(site.id, document, revisionSummary);
    await ensureSiteWorkspaceFromDocument(site.id, result.document);

    return {
      site: result.site,
      document: result.document,
      revision: result.revision,
    };
  } catch (error) {
    const shouldCleanup = await shouldCleanupDraftSite(site.id);

    if (shouldCleanup) {
      try {
        await deleteSite(site.id);
      } catch (cleanupError) {
        console.error('sites:create-draft-cleanup-failed', {
          siteId: site.id,
          message: cleanupError instanceof Error ? cleanupError.message : 'Unknown cleanup error.',
        });
      }
    } else {
      console.warn('sites:create-draft-preserved-after-save-failure', {
        siteId: site.id,
        message: error instanceof Error ? error.message : 'Unknown save error.',
      });
    }

    throw error;
  }
}

async function shouldCleanupDraftSite(siteId: string): Promise<boolean> {
  const state = await apiRequest<{ documentCount: number; revisionCount: number }>(
    `/api/sites/${encodeURIComponent(siteId)}/persistence-state`,
  );
  return state.documentCount === 0 && state.revisionCount === 0;
}

export async function createSiteDraft(input: CreateSiteDraftInput): Promise<SiteSummary> {
  const validated = createSiteDraftInputSchema.parse(input);
  const document = buildInitialSiteDocument(validated);
  const result = await createDraftSiteWithRevision(
    validated,
    document,
    '初期下書きを作成',
  );

  return result.site;
}

export async function createSiteDraftWithDocument(
  input: CreateSiteDraftInput,
  document: SiteDocument,
  revisionSummary: string,
): Promise<{ site: SiteSummary; document: SiteDocument; revision: SiteRevision }> {
  return createDraftSiteWithRevision(input, document, revisionSummary);
}

export async function deleteSite(siteId: string): Promise<void> {
  await apiRequest<void>(`/api/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE' });
}

export async function updateSiteSettings(
  siteId: string,
  input: UpdateSiteSettingsInput,
): Promise<SiteSummary> {
  const validated = updateSiteSettingsInputSchema.parse(input);
  const data = await apiRequest<unknown>(`/api/sites/${encodeURIComponent(siteId)}/settings`, {
    method: 'PATCH',
    body: validated,
  });
  return siteSummarySchema.parse(data);
}

export async function updateSiteCustomDomain(
  siteId: string,
  customDomain: string | null,
): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/sites/${encodeURIComponent(siteId)}/custom-domain`, {
    method: 'PATCH',
    body: { customDomain },
  });
  return siteSummarySchema.parse(data);
}

export async function countCustomDomains(): Promise<number> {
  const data = await apiRequest<{ customDomains: number }>('/api/sites/usage');
  return data.customDomains;
}

export async function generatePreviewToken(siteId: string): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/sites/${encodeURIComponent(siteId)}/preview-token`, {
    method: 'POST',
  });
  return siteSummarySchema.parse(data);
}

export async function revokePreviewToken(siteId: string): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/sites/${encodeURIComponent(siteId)}/preview-token`, {
    method: 'DELETE',
  });
  return siteSummarySchema.parse(data);
}

export async function getSiteByPreviewToken(token: string): Promise<SiteSummary> {
  const data = await apiRequest<unknown>(`/api/public/sites/by-preview-token/${encodeURIComponent(token)}`);
  return siteSummarySchema.parse(data);
}
